import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useMutation } from '@apollo/client';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import { logError } from 'commons/logger';
import CtaButton from 'components/essentials/Cta/CtaButton';
import { SUBMIT_DS_RETAIL_EMAIL_OPTIN } from 'mocks/mutations';
import getStoreDetails from 'commons/getStoreDetails';
import { GET_STORE_EMPLOYEES } from 'mocks/queries';

import LabeledSelectBox from '../LabeledSelectBox';

const DSRetailEmailOptin = ({ isAdmin }) => {
  const [getStoreEmployees, { data }] = useLazyQuery(GET_STORE_EMPLOYEES);
  const [storeData, setStoreData] = useState({});
  const [serviceType, setServiceType] = useState('Virtual');
  const [associate, setAssociate] = useState(null);
  const [email, setEmail] = useState(null);
  const [serviceTypeError, setServiceTypeError] = useState(null);
  const [associateError, setAssociateError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const emailRef = useRef();
  const [submitRetailEmailOptin] = useMutation(SUBMIT_DS_RETAIL_EMAIL_OPTIN);

  useEffect(() => {
    (async () => {
      const storeDetails = getStoreDetails();

      if (!storeDetails) {
        setStoreData({});
        return;
      }

      await getStoreEmployees({
        variables: { storeId: storeDetails.id },
      });

      setStoreData(storeDetails);
    })();
  }, [getStoreEmployees]);

  const { employees } = (data && data.storeEmployees) || {};

  const validateEmail = () => {
    const emailRE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!email) {
      setEmailError('Email address is required');
      return false;
    }
    if (!emailRE.test(email)) {
      setEmailError('Email address is invalid');
      return false;
    }
    return true;
  };

  const onSubmit = async () => {
    if (!associate) {
      setAssociateError('Associate name is required');
      return;
    }
    if (!serviceType) {
      setServiceTypeError('Please select a service type');
      return;
    }
    if (!validateEmail()) {
      if (emailRef.current) {
        emailRef.current.focus();
      }
      return;
    }

    await submitRetailEmailOptin({
      variables: {
        input: {
          store_id: Number(storeData.id),
          entered_by: associate,
          customer_email: email,
          design_service_requested: 1,
          design_service_type: serviceType,
          swatch_kit_recieved: 0,
        },
      },
    }).catch(e => {
      logError(e);
    });
    setSubmitted(true);
  };

  if (isAdmin) {
    return (
      <div className="w-full p-12 border border-solid [border-color:#ccc] [&_p]:text-center">
        <p>
          Design Services Retail-email-optin component. The component will
          display correctly in Consumer
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="px-4 md:px-20">
        <p className="text-xl text-center w-full py-6">
          Thank you for signing up!
        </p>
      </div>
    );
  }

  return (
    storeData?.id && (
      <div className="px-4 md:px-16 lg:px-24">
        <p className="text-2xl font-bold text-center w-full pb-6">
          Sign up for Free Design Services
        </p>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4 lg:gap-8">
          <div>
            <LabeledSelectBox
              label="Select Associate"
              options={
                employees && employees.length > 0
                  ? employees.map(employee => ({
                      value: employee.real_name,
                      label: employee.real_name,
                    }))
                  : []
              }
              errorMessage={associateError}
              value={associate}
              onChange={evt => {
                setAssociate(evt.target.value);
                setAssociateError(null);
              }}
            />
          </div>
          <div>
            <LabeledSelectBox
              label="Design Services"
              options={[
                { value: 'Virtual', label: 'Virtual' },
                { value: 'In Store', label: 'In Store' },
                { value: 'In Home', label: 'In Home' },
              ]}
              showBlankOption={false}
              value={serviceType}
              errorMessage={serviceTypeError}
              onChange={evt => {
                setServiceType(evt.target.value);
                setServiceTypeError(null);
              }}
            />
          </div>
          <div className="col-span-2">
            <LabeledTextInput
              label="E-mail"
              id="email"
              type="email"
              name="email"
              ref={emailRef}
              value={email}
              onChange={evt => {
                setEmail(evt.target.value);
                setEmailError(null);
              }}
              errorMessage={emailError}
              action={
                <CtaButton
                  block
                  styling="solid-button"
                  color="brand"
                  onClick={onSubmit}
                >
                  Submit
                </CtaButton>
              }
              actionHasPadding={false}
              actionStretchHeight
            />
          </div>
        </div>
      </div>
    )
  );
};

DSRetailEmailOptin.propTypes = {
  isAdmin: PropTypes.bool,
};

export default DSRetailEmailOptin;
